import { lazy } from 'react';
import { TenantRouteMeta } from '../types';

const Actions = lazy(() => import('../../pages/Actions/Actions'));
const RequireSuperAdmin = lazy(() => import('../../auth/RequireSuperAdmin'));
const ActionEdit = lazy(() => import('../../pages/Actions/ActionEdit'));
const LibraryPage = lazy(() => import('../../pages/Actions/catalog/LibraryPage'));
const CatalogActionDetail = lazy(() => import('../../pages/Actions/catalog/CatalogActionDetail'));
const AdventuresList = lazy(() => import('../../pages/Actions/AdventuresList'));
const TaskV2MetricsPage = lazy(() => import('../../pages/TaskV2/MetricsPage'));
const TaskV2CompletionCriteriaPage = lazy(() => import('../../pages/TaskV2/CompletionCriteriaPage'));
const LibraryV2Page = lazy(() => import('../../pages/TaskV2/LibraryV2Page'));
const LibraryV2DetailPage = lazy(() => import('../../pages/TaskV2/catalog/LibraryV2DetailPage'));
const TenantV2ActionsPage = lazy(() => import('../../pages/TaskV2/TenantV2ActionsPage'));
const TenantV2ActionDetailPage = lazy(() => import('../../pages/TaskV2/TenantV2ActionDetailPage'));
const TenantV2CollectionsPage = lazy(() => import('../../pages/TaskV2/TenantV2CollectionsPage'));
const TenantV2CollectionDetailPage = lazy(() => import('../../pages/TaskV2/TenantV2CollectionDetailPage'));
const SweepstakesList = lazy(() => import('../../pages/Actions/SweepstakesList'));
const QuestionnairesList = lazy(() => import('../../pages/Actions/QuestionnairesList'));
const TermsOfServiceList = lazy(() => import('../../pages/Actions/TermsOfServiceList'));
const TriviaList = lazy(() => import('../../pages/Actions/TriviaList'));
const TriviaEdit = lazy(() => import('../../pages/Actions/TriviaEdit'));
const SurveyEdit = lazy(() => import('../../pages/Surveys/SurveyEdit'));
const CmsComponents = lazy(() => import('../../pages/CmsComponents/CmsComponents'));
const CmsComponentDetail = lazy(() => import('../../pages/CmsComponents/CmsComponentDetail'));
const CmsComponentDetailV2 = lazy(() => import('../../pages/CmsComponents/CmsComponentDetailV2'));
const StoryblokCmsComponents = lazy(() => import('../../pages/CmsComponents/StoryblokCmsComponents'));
const StoryblokCmsComponentDetail = lazy(() => import('../../pages/CmsComponents/StoryblokCmsComponentDetail'));
const HtmlTemplates = lazy(() => import('../../pages/HtmlTemplates/HtmlTemplates'));
const FlexComponents = lazy(() => import('../../pages/FlexComponents/FlexComponents'));
const FlexComponentDetail = lazy(() => import('../../pages/FlexComponents/FlexComponentDetail'));
const FlexPalettePage = lazy(() => import('../../pages/FlexComponents/FlexPalettePage'));
const Notifications = lazy(() => import('../../pages/Notifications/Notifications'));
// R7: tenant-scoped Scripts surface moved to /admin/scripts. The tenant
// page at src/pages/Scripts/ has been deleted. Tenant-scoped script
// bindings (task-reward-script, event-handlers) are deferred.
const TaskRelationships = lazy(() => import('../../pages/TaskRelationships/TaskRelationships'));
const MemberImport = lazy(() => import('../../pages/MemberImport/MemberImport'));

// Storyblok-backed content management — separate from the legacy CMS group above.
// One parameterized page rendered per content type (FAQ / Action / Agreement)
// matching the customer-portal Content > FAQs reference UI.
const ContentTypePage = lazy(() => import('../../pages/StoryblokContent/ContentTypePage'));
const AgreementsPage = lazy(() => import('../../pages/StoryblokContent/AgreementsPage'));
const PdfToHtml = lazy(() => import('../../pages/FigmaToHtml/PdfToHtml'));
const RequireMasterTenant = lazy(() => import('../../auth/RequireMasterTenant'));

export const contentRoutes: TenantRouteMeta[] = [
  // ── Actions group ───────────────────────────────────────────────
  {
    path: 'actions',
    element: <Actions />,
    permission: 'actions',
    actionsSurface: 'v1',
    sidebar: { group: 'actions', label: 'All Actions', order: 1 },
  },
  {
    path: 'actions/:taskCode',
    element: <ActionEdit />,
    permission: 'actions',
    actionsSurface: 'v1',
    // Detail page — not in sidebar.
  },
  {
    // Master-only action library (the canonical templates other tenants import).
    // Sibling path — NOT `actions/library` — so the sidebar active-state
    // highlights Library exactly; a nested path would also light "All Actions".
    // Master gating: `masterOnly` flag + LibraryPage route guard (NotFound on non-master).
    path: 'action-library',
    element: <LibraryPage />,
    permission: 'actions',
    actionsSurface: 'v1',
    sidebar: { group: 'actions', label: 'Library', order: 0, masterOnly: true },
  },
  {
    // Catalogue-action detail — more specific than `actions/:taskCode`, so it matches first.
    path: 'actions/detail/:actionId',
    element: <CatalogActionDetail />,
    permission: 'actions',
    actionsSurface: 'v1',
    // Detail page — not in sidebar.
  },
  {
    // Same CatalogActionDetail reached FROM the master Library.
    path: 'action-library/detail/:actionId',
    element: <CatalogActionDetail />,
    permission: 'actions',
    actionsSurface: 'v1',
    // Detail page — not in sidebar.
  },
  {
    path: 'adventures',
    element: <AdventuresList />,
    permission: 'actions',
    actionsSurface: 'v1',
    // Hidden when the tenant is on Task V2 (adventures are not part of the V2 model).
    sidebar: { group: 'actions', label: 'Adventures', order: 2, hideWhenTaskV2: true },
  },
  // ── Task V2 group ───────────────────────────────────────────────
  // Flag off: gated by the `taskv2` capability role (RequireRole enforces the
  // route `role`; the 'task-v2' sidebar group's requiredRole hides it).
  // Flag on: gated by the `task_v2` permission (grantable, catalog expansion) —
  // `role: 'taskv2'` metadata is KEPT so the flag-off path is untouched.
  // Super-admins (incl. the local-dev bypass user) pass automatically either
  // way. Independent of the `isTaskV2Enabled` TENANT attribute (which still
  // scopes All Actions V2).
  {
    // Task V2 global vocabulary — MASTER-ONLY (a global authoring surface, like
    // Library V2). Hidden from the sidebar off-master; RequireMasterTenant 404s
    // a direct URL on a non-master tenant. The data is one shared global set
    // (config.metric_definition_v2), not tenant-scoped.
    path: 'task-v2/metrics',
    element: <RequireMasterTenant><TaskV2MetricsPage /></RequireMasterTenant>,
    role: 'taskv2',
    permission: 'task_v2',
    // No actionsSurface: a master-only GLOBAL surface, not this tenant's actions.
    sidebar: { group: 'task-v2', label: 'V2 Metrics', order: 3, masterOnly: true },
  },
  {
    path: 'task-v2/criteria',
    element: <RequireMasterTenant><TaskV2CompletionCriteriaPage /></RequireMasterTenant>,
    role: 'taskv2',
    permission: 'task_v2',
    // No actionsSurface: a master-only GLOBAL surface, not this tenant's actions.
    sidebar: { group: 'task-v2', label: 'V2 Completion Criteria', order: 4, masterOnly: true },
  },
  {
    // The independent Task V2 action library (master tenant), separate from the V1 Library.
    path: 'task-v2/library',
    element: <LibraryV2Page />,
    role: 'taskv2',
    permission: 'task_v2',
    // No actionsSurface: the global library is authored on the master tenant
    // whatever that tenant's own actionsDisplayMode is. With the gate, MasterTenant
    // on 'actions' (dev01) bounced every Task V2 library link to Actions.
    sidebar: { group: 'task-v2', label: 'Library V2', order: 1, masterOnly: true },
  },
  {
    path: 'task-v2/library/detail/:actionId',
    element: <LibraryV2DetailPage />,
    role: 'taskv2',
    permission: 'task_v2',
    // No actionsSurface, same as the library list.
    // Detail page — not in sidebar.
  },
  {
    // A tenant's imported Task V2 actions — only shown when the tenant has Task V2 enabled.
    path: 'task-v2/actions',
    element: <TenantV2ActionsPage />,
    role: 'taskv2',
    permission: 'task_v2',
    actionsSurface: 'v2',
    sidebar: {
      group: 'task-v2', label: 'All Actions V2', order: 2, showWhenTaskV2: true,
      labelWhenV1ActionsHidden: 'All Actions',
    },
  },
  {
    // Collections — composite offerings built from this tenant's published actions.
    path: 'task-v2/collections',
    element: <TenantV2CollectionsPage />,
    role: 'taskv2',
    permission: 'task_v2',
    actionsSurface: 'v2',
    sidebar: { group: 'task-v2', label: 'Collections', order: 3, showWhenTaskV2: true },
  },
  {
    path: 'task-v2/collections/:collectionCode',
    element: <TenantV2CollectionDetailPage />,
    role: 'taskv2',
    permission: 'task_v2',
    actionsSurface: 'v2',
    // Detail page — not in sidebar.
  },
  {
    path: 'task-v2/actions/:taskCode',
    element: <TenantV2ActionDetailPage />,
    role: 'taskv2',
    permission: 'task_v2',
    actionsSurface: 'v2',
    // Detail page — not in sidebar.
  },
  // Questionnaires/trivia/sweepstakes/terms-of-service/task-relationships are
  // reached FROM the Actions surface (e.g. "Configure trivia questions" on a
  // trivia-type task) and are de-facto public today — decisions §29 confirms
  // these are part of `actions`, not super-admin-only.
  {
    path: 'questionnaires',
    element: <QuestionnairesList />,
    role: 'admin',
    permission: 'actions',
  },
  // Trivia is configured from within Actions (the "Configure trivia questions"
  // action on a trivia-type task's edit page), not via its own sidebar entry.
  {
    path: 'trivia',
    element: <TriviaList />,
    role: 'admin',
    permission: 'actions',
    // No sidebar entry — reachable from a trivia task in All Actions.
  },
  {
    path: 'trivia/:triviaCode',
    element: <TriviaEdit />,
    role: 'admin',
    permission: 'actions',
    // Detail/editor page — not in sidebar.
  },
  // Survey authoring (PLT-3143) — reached FROM a Survey-type action's
  // "Configure survey questions" launcher (recon model: author-from-the-action,
  // like trivia), NOT a standalone tab. Editor route only; persists a per-browser
  // LOCAL DRAFT (server delivery is a TODO(enabler)).
  {
    path: 'surveys/:code',
    element: <SurveyEdit />,
    role: 'admin',
    permission: 'actions',
    // Detail/editor page — reached from the action, not in sidebar.
  },
  {
    path: 'sweepstakes',
    element: <SweepstakesList />,
    role: 'admin',
    permission: 'actions',
  },
  {
    path: 'terms-of-service',
    element: <TermsOfServiceList />,
    role: 'admin',
    permission: 'actions',
  },
  {
    path: 'task-relationships',
    element: <TaskRelationships />,
    role: 'admin',
    permission: 'actions',
  },

  // ── CMS group (legacy custom CMS) ───────────────────────────────
  // CPT-1219: `storyblok: 'legacy'` — this whole group (Components AND
  // HTML Templates) is hidden and unreachable for Storyblok tenants, so a
  // tenant only ever sees one content surface. The tenant attribute is the
  // sole gate; the `permission` below is the separate CPT-1174 RBAC layer.
  {
    path: 'cms-components',
    storyblok: 'legacy',
    element: <CmsComponents />,
    permission: 'cms',
    sidebar: { group: 'cms', label: 'Components', order: 1 },
  },
  {
    path: 'cms-components/:componentCode',
    storyblok: 'legacy',
    element: <CmsComponentDetail />,
    permission: 'cms',
  },
  {
    path: 'cms-components-v2/:componentCode',
    storyblok: 'legacy',
    element: <CmsComponentDetailV2 />,
    permission: 'cms',
  },
  {
    path: 'html-templates',
    storyblok: 'legacy',
    element: <HtmlTemplates />,
    permission: 'cms',
    sidebar: { group: 'cms', label: 'HTML Templates', order: 2 },
  },

  // ── Content group (Storyblok-backed) ────────────────────────────
  // CPT-1219: `storyblok: 'only'` — the mirror of the CMS group above.
  //
  // CPT-1375: FAQs and Agreements are ALSO listed under Flex Components
  // (`sidebar.alsoIn`); Cards is not (MNS-1195). One route, one page, one row of data — two nav entries.
  // They must appear under Flex even when the Storyblok setting is OFF and this
  // whole group is hidden, so a tenant live on Content (Old) can build a Flex
  // renewal without switching its live content over (AC6, the KP case). Hence
  // `ignoreStoryblokGate` on the second placement only: the first placement
  // stays gated, so the tenant still sees exactly one CONTENT surface.
  // One sidebar entry per content type. Each entry renders the same
  // ContentTypePage, parameterized by :contentTypeCode in the URL.
  {
    path: 'storyblok-content/cms-components',
    storyblok: 'only',
    element: <StoryblokCmsComponents />,
    permission: 'content',
    sidebar: { group: 'storyblok-content', label: 'CMS Components', order: 0 },
  },
  {
    path: 'storyblok-content/cms-components/:categoryCode/:itemId',
    storyblok: 'only',
    // PLT-3634: Flex Components lists Onboarding Splash and Purse Details V2
    // whatever the Storyblok setting, and links here.
    flexSurface: true,
    element: <StoryblokCmsComponentDetail />,
    // MNS-1275: Flex Components links here too, so a Flex user without the
    // Content permission must still be able to open it.
    permissionAnyOf: ['content', 'flex_components'],
  },
  {
    path: 'storyblok-content/faqs',
    storyblok: 'only',
    element: <ContentTypePage contentTypeCode="faq" />,
    // MNS-1275: also listed under Flex Components, so Flex users reach it too.
    permissionAnyOf: ['content', 'flex_components'],
    sidebar: {
      group: 'storyblok-content', label: 'FAQs', order: 1,
      // CPT-1375 AC5/AC6: the SAME page, also listed under Flex Components,
      // and reachable there whether the Storyblok setting is on or off.
      alsoIn: { group: 'flex-components', label: 'FAQs', order: 2, ignoreStoryblokGate: true },
    },
  },
  {
    path: 'storyblok-content/cards',
    storyblok: 'only',
    element: <ContentTypePage contentTypeCode="card" />,
    permission: 'content',
    // MNS-1195: not also listed under Flex Components. Cards are configured in
    // the "How Rewards Content" flex component, so a Cards tab there misleads.
    sidebar: { group: 'storyblok-content', label: 'Cards', order: 2 },
  },
  {
    path: 'storyblok-content/agreements',
    storyblok: 'only',
    element: <AgreementsPage />,
    // MNS-1275: also listed under Flex Components, so Flex users reach it too.
    permissionAnyOf: ['content', 'flex_components'],
    sidebar: {
      group: 'storyblok-content', label: 'Agreements', order: 3,
      // CPT-1375 AC5/AC6: the SAME page, also listed under Flex Components,
      // and reachable there whether the Storyblok setting is on or off.
      alsoIn: { group: 'flex-components', label: 'Agreements', order: 4, ignoreStoryblokGate: true },
    },
  },

  // ── Flex Components group (flex-shell-ui components) ─────────────
  // Per-tenant flex components + variants rendered live from
  // @sunnyrewards/flex-shell-ui, with color editing + Storyblok content.
  {
    path: 'flex-components',
    element: <FlexComponents />,
    role: 'flex',
    permission: 'flex_components',
    sidebar: { group: 'flex-components', label: 'Components', order: 0 },
  },
  {
    // Static segment — declared before `:familyKey` so it matches first.
    path: 'flex-components/palette',
    element: <FlexPalettePage />,
    role: 'flex',
    permission: 'flex_components',
    sidebar: { group: 'flex-components', label: 'Palette', order: 1 },
  },
  {
    path: 'flex-components/:familyKey',
    element: <FlexComponentDetail />,
    role: 'flex',
    permission: 'flex_components',
    // Detail page — not in sidebar.
  },

  // ── Notifications ────────────────────────────────────────────────
  {
    path: 'notifications',
    element: <Notifications />,
    role: 'admin',
    permission: 'notifications',
    sidebar: { group: 'notifications', label: 'Notifications' },
  },

  // R7: Scripts moved to /admin/scripts. Tenant route retired.

  // ── PDF to HTML ──────────────────────────────────────────────────
  // MasterTenant-only via `sidebar.masterOnly` (unrelated to the permission
  // gate). The `RequireSuperAdmin` element wrapper + per-item `adminOnly`
  // sidebar flag this section carried were BOTH hard-coded to `isSuperAdmin`
  // independent of the RBAC_V2 flag/permission — a `pdf_to_html` grant would
  // never have actually unlocked this page. Removed as part of the catalog
  // expansion so `permission: 'pdf_to_html'` (via RequirePermission, wired
  // generically in AppRoutes.tsx) is the ONLY gate, same as every other
  // grantable section.
  {
    path: 'pdf-to-html',
    // Shipped Super-Admin-locked; `unlessPermission` opens it to pdf_to_html
    // holders under RBAC_V2 while flag-off stays byte-identical (adminOnly
    // sidebar item + super-admin wall).
    element: (
      <RequireSuperAdmin unlessPermission="pdf_to_html">
        <PdfToHtml />
      </RequireSuperAdmin>
    ),
    permission: 'pdf_to_html',
    sidebar: { group: 'pdf-to-html', label: 'PDF to HTML', order: 1, adminOnly: true, masterOnly: true },
  },

  // ── Member Import ────────────────────────────────────────────────
  {
    path: 'member-import',
    element: <MemberImport />,
    permission: 'member_import',
    sidebar: { group: 'member-import', label: 'Member Import' },
  },
];
